import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { trpc, type RouterOutputs } from "@/lib/trpc";
import { useCurrencyFormatter } from "@/hooks/useCurrencyFormatter";
import { formatDay, useDateFnsLocale } from "@/lib/format-dates";
import type { ErrorTableColumn } from "../bulk-stock-upload/ErrorTable";
import ChangesRowShell from "./ChangesRowShell";

/**
 * Expanded row of a client price CSV import in Logs > Adjustments: every price
 * it wrote, one line per SKU and price list, with the price before and after
 * and the day it counts from. The states and the table (search, the full-set
 * CSV download) are ChangesRowShell's. Prices are in the org's own currency
 * (only the 3PL that imported them can open the row), so the formatter shows
 * them without conversion.
 */

type Change = RouterOutputs["logs"]["getClientPriceChanges"][number];

const ClientPriceChangesRow = ({ batchId }: { batchId: string }) => {
  const { t } = useTranslation("inventory");
  const { format } = useCurrencyFormatter();
  const dateLocale = useDateFnsLocale();
  // An applied import never changes, so the list is fetched once per batch.
  const changesQuery = trpc.logs.getClientPriceChanges.useQuery(
    { batchId },
    { staleTime: Infinity }
  );

  const columns = useMemo<ErrorTableColumn<Change>[]>(() => {
    const list = (c: Change) =>
      c.clientOrgId === null ? t("clientPrices.grid.allClients") : (c.clientName ?? "-");
    // A cleared client price falls back to the All clients price; a cleared
    // All clients price leaves no price.
    const after = (c: Change) =>
      c.to !== null
        ? format(c.to)
        : c.clientOrgId === null
          ? t("clientPrices.grid.meta.noPrice")
          : t("clientPrices.grid.meta.allClients");
    return [
      {
        key: "sku",
        header: t("clientPrices.logs.col.sku"),
        className: "w-[30%]",
        cellClassName: "break-all font-mono",
        cell: (c) => c.sku,
        csv: (c) => c.sku,
        search: (c) => c.sku,
      },
      {
        key: "list",
        header: t("clientPrices.logs.col.list"),
        className: "w-[28%]",
        cellClassName: "[overflow-wrap:anywhere]",
        cell: list,
        csv: list,
        search: list,
      },
      {
        key: "from",
        header: t("clientPrices.logs.col.from"),
        className: "w-[14%] text-right",
        cellClassName: "tabular-nums text-muted-foreground",
        cell: (c) => (c.from === null ? t("clientPrices.grid.meta.noPrice") : format(c.from)),
        csv: (c) => c.from ?? "",
      },
      {
        key: "to",
        header: t("clientPrices.logs.col.to"),
        className: "w-[14%] text-right",
        cellClassName: "tabular-nums",
        cell: after,
        csv: (c) => c.to ?? "",
      },
      {
        key: "countsFrom",
        header: t("clientPrices.logs.col.countsFrom"),
        className: "w-[14%]",
        cellClassName: "tabular-nums text-muted-foreground",
        cell: (c) => formatDay(c.effectiveFrom, dateLocale),
        csv: (c) => c.effectiveFrom,
      },
    ];
  }, [t, format, dateLocale]);

  return (
    <ChangesRowShell<Change>
      query={changesQuery}
      columns={columns}
      rowKey={(c) => `${c.skuId}:${c.clientOrgId ?? "all"}`}
      filename={`client-price-changes-${batchId}.csv`}
      searchPlaceholder={t("clientPrices.logs.search")}
      texts={{
        error: t("clientPrices.logs.error"),
        retry: t("common:retry"),
        empty: t("clientPrices.logs.empty"),
        more: ({ hidden }) => t("clientPrices.bulk.more", { count: hidden }),
      }}
    />
  );
};

export default ClientPriceChangesRow;
